import { lazy } from 'react';

/**
 * lazyWithRetry — drop-in replacement for React.lazy that recovers from
 * "ChunkLoadError" / "Loading chunk X failed" that happens after a fresh
 * deploy invalidates the hashed JS/CSS filenames referenced by a stale
 * `index.html` still cached in the user's browser.
 *
 *   - First failure  → set a sessionStorage flag + hard-reload the page so
 *                      the browser fetches the freshly-hashed bundle.
 *   - Second failure → give up (do NOT loop) and rethrow so the normal
 *                      React error boundary shows an error instead of a
 *                      blank white screen.
 */
const RELOAD_FLAG = 'chunk_reload_attempted';

function isChunkError(err) {
  const msg = String(err?.message || err);
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\d\w]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    // SPA-fallback: index.html served for missing chunk → HTML parsed as JS.
    /Unexpected token ['"]?<['"]?/i.test(msg)
  );
}

async function runImport(importer) {
  try {
    const mod = await importer();
    try { window.sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
    return mod;
  } catch (err) {
    if (isChunkError(err)) {
      let alreadyTried = false;
      try { alreadyTried = window.sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { /* ignore */ }
      if (!alreadyTried) {
        try { window.sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* ignore */ }
        // Force a hard reload — the new index.html will pull the new chunks.
        window.location.reload();
        // Return a no-op component while the reload is in flight.
        return { default: () => null };
      }
    }
    throw err;
  }
}

export default function lazyRetry(importer) {
  return lazy(() => runImport(importer));
}
